import React, { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';
import { ApiError, ApiErrorType } from '../../hooks/useApi';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  margin: 2rem;
  border-radius: ${props => props.theme.borderRadius.medium};
  background-color: ${props => props.theme.colors.errorLight};
  border: 1px solid ${props => props.theme.colors.error};
  color: ${props => props.theme.colors.text.dark};
`;

const ErrorTitle = styled.h2`
  color: ${props => props.theme.colors.error};
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.p`
  margin-bottom: 1rem;
  font-size: ${props => props.theme.typography.fontSize.medium};
`;

const ErrorDetails = styled.details`
  margin-top: 1rem;
  width: 100%;
  
  summary {
    cursor: pointer;
    color: ${props => props.theme.colors.primary};
    font-weight: ${props => props.theme.typography.fontWeight.semiBold};
    margin-bottom: 0.5rem;
  }
  
  pre {
    background-color: ${props => props.theme.colors.backgroundDark};
    padding: 1rem;
    border-radius: ${props => props.theme.borderRadius.small};
    overflow: auto;
    color: ${props => props.theme.colors.text.light};
    font-size: ${props => props.theme.typography.fontSize.small};
  }
`;

const ResetButton = styled.button`
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.small};
  cursor: pointer;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  
  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
  }
`;

/**
 * Error Boundary component to catch JavaScript errors anywhere in the child component tree,
 * log those errors, and display a fallback UI instead of crashing the whole app.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to an error reporting service
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    
    this.setState({
      errorInfo
    });
    
    // Here you could also send the error to your analytics or error tracking service
    // Example: sendToErrorTracking(error, errorInfo);
  }

  resetErrorBoundary = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // If a custom fallback is provided, use it
      if (fallback) {
        return fallback;
      }

      // Default error UI
      let errorMessage = 'An unexpected error occurred';
      let errorType = 'Application Error';
      
      // Handle API errors specifically
      if (error instanceof ApiError) {
        switch (error.type) {
          case ApiErrorType.NETWORK:
            errorType = 'Network Error';
            errorMessage = 'Unable to connect to the server. Please check your internet connection.';
            break;
          case ApiErrorType.UNAUTHORIZED:
            errorType = 'Authentication Error';
            errorMessage = 'You need to sign in to access this feature.';
            break;
          case ApiErrorType.FORBIDDEN:
            errorType = 'Permission Error';
            errorMessage = 'You do not have permission to access this resource.';
            break;
          case ApiErrorType.NOT_FOUND:
            errorType = 'Not Found';
            errorMessage = 'The requested resource could not be found.';
            break;
          case ApiErrorType.VALIDATION:
            errorType = 'Validation Error';
            errorMessage = error.message || 'The submitted data is invalid.';
            break;
          case ApiErrorType.SERVER:
            errorType = 'Server Error';
            errorMessage = 'The server encountered an error. Please try again later.';
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
      } else if (error) {
        errorMessage = error.message || errorMessage;
      }

      return (
        <ErrorContainer>
          <ErrorTitle>{errorType}</ErrorTitle>
          <ErrorMessage>{errorMessage}</ErrorMessage>
          
          {errorInfo && (
            <ErrorDetails>
              <summary>View Technical Details</summary>
              <pre>
                {error?.toString()}
                <br />
                {errorInfo.componentStack}
              </pre>
            </ErrorDetails>
          )}
          
          <ResetButton onClick={this.resetErrorBoundary}>
            Try Again
          </ResetButton>
        </ErrorContainer>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
